import React, { forwardRef, useId } from 'react';
import { Icon, type IconName } from '../primitives/Icon';
import type { ComponentProps, ReactNode } from 'react';
import type { TextInput as RNTextInput } from 'react-native';
import { Text, XStack, YStack } from 'tamagui';
import { Input } from '../primitives/Input';
import { useThemeColors } from '../hooks/useThemeColors';
import {
  RowFocusContext,
  useFieldFocusContext,
} from '../hooks/useRowFocus';
import { FormMessage } from './FormMessage';

/**
 * Form primitives that codify the new-design secondary-screen pattern
 * (ADR-0050). Use these instead of hand-rolled XStack/YStack so every
 * detail screen renders the same icon-label-input row, divider, and
 * surface card.
 *
 * Layout:
 *
 *   <FormSection>
 *     <FormRow icon="hotel" label="Name">
 *       <FormInput value={...} onChangeText={...} />
 *     </FormRow>
 *     <FormRow icon="place" label="Address" required error={addressError}>
 *       <FormInput value={...} onChangeText={...} />
 *     </FormRow>
 *   </FormSection>
 *
 * `FormSection` inserts a divider between siblings automatically.
 *
 * Per ADR-0075, `FormRow` now publishes the same `RowFocusContext`
 * field-context object that `FormRow` does, so wrapped inputs pick
 * up `error` / `hint` / `disabled` / `aria-describedby` automatically.
 * `error`, `hint`, and `required` are additive — existing call sites
 * keep working without changes.
 */

export interface FormRowProps {
  /** MaterialIcons name rendered in the 36x36 leading slot. */
  icon: IconName;
  /** Uppercase-cased small label rendered above the input. */
  label: string;
  /** Renders `*` in `$textError` after the label. */
  required?: boolean;
  /** Helper text shown below the row when no error is present. */
  hint?: string;
  /** Error message — replaces hint, announces via role="alert", paints child border. */
  error?: string | null;
  /** Greys out the row + propagates to child input. */
  disabled?: boolean;
  children: ReactNode;
}

export function FormRow({ icon, label, required, hint, error, disabled, children }: FormRowProps) {
  const theme = useThemeColors();
  const id = useId();
  const controlId = id;
  const messageId = `${id}-message`;
  const hasError = Boolean(error);

  const { focused, value } = useFieldFocusContext({
    controlId,
    messageId,
    error: hasError,
    ...(hint !== undefined ? { hint } : null),
    ...(disabled !== undefined ? { disabled } : null),
  });

  return (
    <RowFocusContext.Provider value={value}>
      <YStack opacity={disabled ? 0.6 : 1}>
        <XStack
          alignItems="center"
          paddingHorizontal={12}
          paddingVertical={10}
          gap={14}
          borderRadius={14}
          borderWidth={2}
          borderColor={hasError ? '$borderError' : focused ? '$borderFocus' : 'transparent'}
        >
          <YStack
            width={36}
            height={36}
            borderRadius={12}
            backgroundColor="$surfaceCanvas"
            alignItems="center"
            justifyContent="center"
          >
            <Icon name={icon} size={18} color={theme.textOnSurface} />
          </YStack>
          <YStack flex={1} minWidth={0}>
            <XStack alignItems="center" gap={2}>
              <Text
                fontSize={11}
                color={hasError ? '$textError' : '$textSubdued'}
                fontWeight="600"
              >
                {label}
              </Text>
              {required ? (
                <Text fontSize={11} fontWeight="700" color="$textError">
                  *
                </Text>
              ) : null}
            </XStack>
            {children}
          </YStack>
        </XStack>
        {hasError || hint ? (
          <YStack paddingHorizontal={12} paddingTop={2}>
            <FormMessage
              id={messageId}
              {...(hasError
                ? { type: 'error' as const, message: error ?? null }
                : { type: 'info' as const, message: hint ?? null })}
            />
          </YStack>
        ) : null}
      </YStack>
    </RowFocusContext.Provider>
  );
}

export interface FormSectionProps {
  children: ReactNode;
}

/**
 * `$surface` card with `borderRadius={18}`. Inserts a `FormDivider`
 * between each top-level child so callers don't have to remember.
 */
export function FormSection({ children }: FormSectionProps) {
  // Inject FormDivider between siblings. Falsy children (e.g.
  // `condition && <FormRow />`) are skipped so conditional rows don't
  // produce orphan dividers.
  const items = React.Children.toArray(children).filter(Boolean);
  return (
    <YStack backgroundColor="$surface" borderRadius={18} paddingVertical={4}>
      {items.map((child, idx) => (
        <React.Fragment key={(child as React.ReactElement).key ?? `row-${idx}`}>
          {idx > 0 ? <FormDivider /> : null}
          {child}
        </React.Fragment>
      ))}
    </YStack>
  );
}

/** 1px subdued divider with the row's inset so it doesn't touch the card edge. */
function FormDivider() {
  return <YStack height={1} backgroundColor="$dividerSubtle" marginHorizontal={16} />;
}

/** Section header — bold 15px on `$textOnSurface`. Pairs with `FormSection`. */
export interface SectionLabelProps extends ComponentProps<typeof Text> {
  children: ReactNode;
}

export function SectionLabel({ children, ...rest }: SectionLabelProps) {
  return (
    <Text fontSize={15} fontWeight="700" color="$textOnSurface" {...rest}>
      {children}
    </Text>
  );
}

/**
 * Borderless input styled to slot inside a `FormRow`. Routes through the
 * shared `Input` primitive (variant="inline") so every field — Stay,
 * Journey, custom-entry detail screens — gets the same 2px focus
 * border and the same outline suppression on web. Anything text-
 * input-shaped in the app should funnel through this or `Input` directly.
 */
export const FormInput = forwardRef<RNTextInput, ComponentProps<typeof Input>>(
  // eslint-disable-next-line @typescript-eslint/no-shadow -- the inner function expression is deliberately named after the const so React DevTools and stack traces show the component name; it is not a real shadow.
  function FormInput(props, ref) {
    return (
      <Input
        ref={ref}
        variant="inline"
        fontSize={15}
        marginTop={2}
        {...props}
      />
    );
  },
);
